'use client';

import cs from 'classnames';
import Link from 'next/link';
import { css, styled } from 'styled-components';

import { ARTICLE_LIST_ID } from '@dvd/blog/components/ArticleList';
import { bodyXs } from '@dvd/blog/tokens/text-styles';
import { visuallyHiddenStyles } from '@lib/styles';
import { trackLink } from '@lib/tracking';
import type { SafeUrl } from '@types';

/**
 * A single rendered slot in the pagination bar, in left-to-right order.
 *
 * - `arrow` — the prev/next (`←`/`→`) controls.
 * - `page` — a numbered page. `hidden` pages are kept in the DOM for SEO but
 *   not shown to users. `current` marks the active page (never hidden).
 * - `ellipsis` — the visible `…` that stands in for a collapsed run of pages.
 *   `side` is the only thing distinguishing the two possible ellipses, and is
 *   used to give them stable React keys.
 */
export type PaginationItem =
  | {
      kind: 'arrow';
      direction: 'prev' | 'next';
      page: number;
      disabled: boolean;
    }
  | { kind: 'page'; page: number; current: boolean; hidden: boolean }
  | { kind: 'ellipsis'; side: 'leading' | 'trailing' };

/** Pages shown on each side of the current page in the sliding window. */
const SIBLING_COUNT = 1;

/** Width of the window centered on the current page (current ± siblings). */
const WINDOW_SIZE = SIBLING_COUNT * 2 + 1;

/**
 * Largest total that still fits without collapsing: the first and last pages,
 * the full window, and the two ellipses they would otherwise replace. At or
 * below this, every page shows and nothing is hidden.
 */
const MAX_VISIBLE_SLOTS = 2 + WINDOW_SIZE + 2;

/**
 * Build the ordered list of pagination slots for a paginated collection.
 *
 * A fixed-width window of `WINDOW_SIZE` pages slides with the current page,
 * flanked by the first and last pages. The window shifts (rather than shrinks)
 * when it reaches either end, so the leading and trailing pages stay pinned.
 * Pages outside the visible set are still emitted (as `hidden`) so the consumer
 * can render them as crawlable links; a run of two or more hidden pages
 * collapses behind a single ellipsis, while a lone hidden page is revealed
 * instead (an ellipsis there would hide it without saving space). When the
 * total is small enough (≤ `MAX_VISIBLE_SLOTS`) every page shows.
 *
 * Returns an empty list when there is nothing to paginate (one page or fewer).
 */
export const getPaginationItems = (
  totalPages: number,
  currentPage: number,
): PaginationItem[] => {
  if (totalPages < 2) {
    return [];
  }

  const current = Math.min(Math.max(currentPage, 1), totalPages);

  const page = (pageNumber: number, hidden: boolean): PaginationItem => ({
    kind: 'page',
    page: pageNumber,
    current: pageNumber === current,
    hidden,
  });

  const items: PaginationItem[] = [
    {
      kind: 'arrow',
      direction: 'prev',
      page: current - 1,
      disabled: current === 1,
    },
  ];

  if (totalPages <= MAX_VISIBLE_SLOTS) {
    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
      items.push(page(pageNumber, false));
    }
  } else {
    const visible = new Set([1, totalPages]);

    // Fixed-width window centered on the current page, clamped to stay strictly
    // between the pinned first and last pages — it shifts (rather than shrinking
    // or overlapping the ends) so the visible count stays constant.
    const windowStart = Math.min(
      Math.max(current - SIBLING_COUNT, 2),
      totalPages - WINDOW_SIZE,
    );
    for (let offset = 0; offset < WINDOW_SIZE; offset++) {
      visible.add(windowStart + offset);
    }

    // Reveal any lone page wedged between two visible pages — an ellipsis there
    // would occupy the same slot while hiding it.
    const sorted = [...visible].sort((a, b) => a - b);
    for (let index = 0; index < sorted.length - 1; index++) {
      if (sorted[index + 1] - sorted[index] === 2) {
        visible.add(sorted[index] + 1);
      }
    }

    // Emit every page in order (hidden ones stay crawlable), dropping a single
    // ellipsis wherever a run of hidden pages is collapsed.
    let inHiddenRun = false;
    for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
      if (visible.has(pageNumber)) {
        if (inHiddenRun) {
          items.push({
            kind: 'ellipsis',
            side: pageNumber <= current ? 'leading' : 'trailing',
          });
          inHiddenRun = false;
        }
        items.push(page(pageNumber, false));
      } else {
        items.push(page(pageNumber, true));
        inHiddenRun = true;
      }
    }
  }

  items.push({
    kind: 'arrow',
    direction: 'next',
    page: current + 1,
    disabled: current === totalPages,
  });

  return items;
};

/**
 * Resolve the relative href for a page. Page 1 is the canonical base; later
 * pages live under `/page/<n>`. Every href anchors to the article list so
 * paging lands the visitor on the articles, not the top of the page.
 */
export const getPageHref = (baseUrl: SafeUrl, page: number): string =>
  `${page <= 1 ? baseUrl : `${baseUrl}/page/${page}`}#${ARTICLE_LIST_ID}`;

type PaginationProps = {
  /** Total number of pages, N. */
  totalPages: number;
  /** Currently active page, 1-indexed. */
  currentPage: number;
  /**
   * Relative base for page links, e.g. `/blog`. Page 1 links to the base
   * itself; page n links to `${baseUrl}/page/${n}`.
   */
  baseUrl: SafeUrl;
  /** Optional class name, for extending as a styled-component. */
  className?: string;
};

const TRACKING_CATEGORY = 'blog-pagination';

const Nav = styled.nav`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-block: ${({ theme }) => theme.spacing[2]};
`;

const List = styled.ul`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[0.25]};
  margin: 0;
  padding: 0;
  list-style: none;

  ${({ theme }) => theme.mq.minWidth.large} {
    gap: ${({ theme }) => theme.spacing[0.5]};
  }
`;

const Item = styled.li`
  display: flex;

  &.hidden {
    ${visuallyHiddenStyles}
  }
`;

/**
 * Shared sizing for every pill — page numbers, the ellipsis, and the arrows.
 * Every item gets the same box so all links share one click target.
 */
const pillStyles = css`
  ${bodyXs}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.875rem; /* 30px — the design has no matching spacing token */
  min-height: 1.875rem; /* 30px – enforce a consistent size link chars */
  padding: ${({ theme }) => theme.spacing[0.25]};
  color: ${({ theme }) => theme.colors.blue700};
  text-align: center;
  text-decoration: none;
  border-radius: ${({ theme }) => theme.radius.std};
`;

/** Hover/focus affordance shared by every link, page numbers and arrows alike. */
const linkHover = css`
  transition:
    background-color var(--global-motion),
    color var(--global-motion);

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.colors.blue800};
    background-color: ${({ theme }) => theme.colors.white};
  }
`;

const PageLink = styled(Link)`
  ${pillStyles}
  ${linkHover}
`;

const CurrentPage = styled.span`
  ${pillStyles}
  font-weight: 700;
  background-color: ${({ theme }) => theme.colors.blue200};
`;

const Ellipsis = styled.span`
  ${pillStyles}
`;

const ArrowLink = styled(Link)`
  ${pillStyles}
  ${linkHover}
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue800};
`;

const ArrowDisabled = styled.span`
  ${pillStyles}
  color: ${({ theme }) => theme.colors.grey500};
`;

const itemKey = (item: PaginationItem): string => {
  switch (item.kind) {
    case 'arrow':
      return `arrow-${item.direction}`;
    case 'ellipsis':
      return `ellipsis-${item.side}`;
    case 'page':
      return `page-${item.page}`;
  }
};

const renderItem = (item: PaginationItem, baseUrl: SafeUrl) => {
  switch (item.kind) {
    case 'arrow': {
      const glyph = item.direction === 'prev' ? '←' : '→';
      const label =
        item.direction === 'prev' ? 'Go to previous page' : 'Go to next page';

      if (item.disabled) {
        return <ArrowDisabled aria-hidden="true">{glyph}</ArrowDisabled>;
      }

      return (
        <ArrowLink
          href={getPageHref(baseUrl, item.page)}
          aria-label={label}
          onClick={() => trackLink(TRACKING_CATEGORY, item.direction)}
        >
          <span aria-hidden="true">{glyph}</span>
        </ArrowLink>
      );
    }
    case 'ellipsis':
      return <Ellipsis aria-hidden="true">…</Ellipsis>;
    case 'page': {
      if (item.current) {
        return <CurrentPage aria-current="page">{item.page}</CurrentPage>;
      }

      return (
        <PageLink
          href={getPageHref(baseUrl, item.page)}
          aria-label={`Go to page ${item.page}`}
          onClick={() => trackLink(TRACKING_CATEGORY, `page-${item.page}`)}
        >
          {item.page}
        </PageLink>
      );
    }
  }
};

/**
 * Blog pagination bar.
 *
 * Renders the prev/next arrows, the first and last pages, and a sliding window
 * of pages around the current one. Pages outside the window collapse behind an
 * ellipsis but are still emitted as crawlable, visually-hidden links so the
 * full archive stays discoverable for SEO. Renders nothing when there is only a
 * single page.
 *
 * Five pages stay visible — the first, the last, and a three-page window
 * (current ± 1) — except at the two reveal steps, where a lone collapsed page is
 * shown instead of hidden behind an ellipsis. Behavior for `totalPages = 39`:
 *
 * | currentPage | Visible pages        | Count | Rule                                   |
 * | ----------- | -------------------- | ----- | -------------------------------------- |
 * | 1, 2, 3     | `1 2 3 4 … 39`       | 5     | start pin (≤ 3)                        |
 * | 4           | `1 2 3 4 5 … 39`     | 6     | window 3,4,5; lone page 2 revealed     |
 * | 5           | `1 … 4 5 6 … 39`     | 5     | sliding window                         |
 * | 20          | `1 … 19 20 21 … 39`  | 5     | centered window                        |
 * | 35          | `1 … 34 35 36 … 39`  | 5     | sliding window                         |
 * | 36          | `1 … 35 36 37 38 39` | 6     | window 35,36,37; lone page 38 revealed |
 * | 37, 38, 39  | `1 … 36 37 38 39`    | 5     | end pin (≥ N−2)                        |
 */
const Pagination = ({
  totalPages,
  currentPage,
  baseUrl,
  className,
}: PaginationProps) => {
  const items = getPaginationItems(totalPages, currentPage);

  if (items.length === 0) {
    return null;
  }

  return (
    <Nav className={className} aria-label="Blog pagination">
      <List>
        {items.map((item) => (
          <Item
            key={itemKey(item)}
            className={cs({ hidden: item.kind === 'page' && item.hidden })}
          >
            {renderItem(item, baseUrl)}
          </Item>
        ))}
      </List>
    </Nav>
  );
};

export default Pagination;
